import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

import { Eye, Printer, Smartphone, Camera } from 'lucide-react';
import axios from 'axios';

export const ConsentsTab = ({ consentsOnly, dashboardData, error, fmtDateUS, loadDashboard, navigate, printPatientReport, safeText, setScanConsentModal, showSmsSelectionModal, toast }) => (
  <>
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => printPatientReport({
            tabKey: 'consents',
            reportTitle: 'Informed Consents Report',
            subtitle: 'Patient signed informed-consent audit summary',
            itemNoun: 'consent',
            items: consentsOnly,
            headerColor: '#4f46e5',
            columns: [
              { key: 'name', label: 'Consent' },
              { key: 'dentist', label: 'Provider' },
              { key: 'assigned', label: 'Assigned' },
              { key: 'signed', label: 'Signed' },
              { key: 'audit', label: 'Audit' },
              { key: 'status', label: 'Status' },
            ],
            rowFor: (c) => {
              const isSigned = c.status === 'signed' || c.status === 'signed_in_office';
              const badge = isSigned
                ? `<span class="badge badge-green">SIGNED${c.status === 'signed_in_office' ? ' (IN-OFFICE)' : ''}</span>`
                : '<span class="badge badge-amber">PENDING</span>';
              const audit = isSigned
                ? [c.signedIpAddress ? `IP: ${c.signedIpAddress}` : '', c.signedAtTimezone || ''].filter(Boolean).join(' · ') || '—'
                : '—';
              return [
                `<strong>${safeText(c.consentName)}</strong>`,
                safeText(c.dentistName),
                fmtDateUS(c.assignedDate),
                isSigned ? fmtDateUS(c.signedAt) : '—',
                audit,
                badge,
              ];
            },
            stats: (items) => [
              { label: 'Total Consents', value: items.length },
              { label: 'Signed', value: items.filter((x) => x.status === 'signed' || x.status === 'signed_in_office').length, color: '#16a34a' },
              { label: 'Pending', value: items.filter((x) => !(x.status === 'signed' || x.status === 'signed_in_office')).length, color: '#d97706' },
            ],
          })}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-md"
          data-testid="consents-print-report-btn"
        >
          <Printer className="h-3.5 w-3.5" />
          Print Consents Report
        </button>
      </div>
      {consentsOnly.map((consent) => (
        <div key={consent.id} className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
          {/* Row 1: Info + Status */}
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-base text-indigo-900">{consent.consentName}</p>
              <p className="text-sm text-gray-600">
                {consent.dentistName && consent.dentistName.startsWith('Dr.') 
                  ? consent.dentistName 
                  : `Dr. ${consent.dentistName}`}
                <span className="text-gray-400 ml-2">{new Date(consent.assignedDate).toLocaleDateString()}</span>
              </p>
              <p className="text-xs text-gray-500">Patient: {consent.patientName || ''}</p>
            </div>
            <div className="flex flex-col items-end gap-1.5 ml-3 flex-shrink-0">
              {consent.status === 'signed' ? (
                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300" data-testid={`consent-signed-${consent.id}`}>
                  Signed {consent.signedAt ? new Date(consent.signedAt).toLocaleDateString() : ''}
                </Badge>
              ) : consent.status === 'signed_in_office' ? (
                <>
                  <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300" data-testid={`consent-signed-office-${consent.id}`}>
                    Signed (In-Office) {consent.signedAt ? new Date(consent.signedAt).toLocaleDateString() : ''}
                  </Badge>
                  {consent.uploadedFile && (
                    <Button
                      data-testid={`view-scan-consent-${consent.id}`}
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('dentalToken');
                          const response = await axios.get(
                            `${process.env.REACT_APP_BACKEND_URL}/api/informed-consent/signed-file/${consent.id}`,
                            { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
                          );
                          const blob = new Blob([response.data], { type: response.headers['content-type'] });
                          const url = URL.createObjectURL(blob);
                          window.open(url, '_blank');
                          setTimeout(() => URL.revokeObjectURL(url), 60000);
                        } catch (err) {
                          toast({ title: "Error", description: "Failed to load signed file", variant: "destructive" });
                        }
                      }}
                      className="text-sm px-3 py-1 h-7 bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View Scan
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300" data-testid={`consent-pending-${consent.id}`}>
                    Pending Signature
                  </Badge>
                  <Button
                    data-testid={`scan-signed-consent-${consent.id}`}
                    size="sm"
                    onClick={() => {
                      const patient = dashboardData?.recentPatients?.find(p => p.id === consent.patientId);
                      setScanConsentModal({
                        open: true,
                        consent: {
                          ...consent,
                          patientName: patient ? `${patient.firstName} ${patient.lastName}` : 'Patient'
                        }
                      });
                    }}
                    className="text-sm px-3 py-1.5 h-8 bg-orange-500 hover:bg-orange-600 text-white font-semibold border-0 shadow-sm"
                  >
                    <Camera className="h-3.5 w-3.5 mr-1" />
                    Scan / Upload Signed
                  </Button>
                </>
              )}
              {consent.emailSent && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"
                  title={`Email sent ${consent.emailSentCount > 1 ? consent.emailSentCount + ' times. ' : ''}Last sent: ${consent.lastEmailSentAt ? new Date(consent.lastEmailSentAt).toLocaleString() : new Date(consent.emailSentAt).toLocaleString()}`}>
                  Email Sent{consent.emailSentCount > 1 ? ` (${consent.emailSentCount}x)` : ''}
                </Badge>
              )}
            </div>
          </div>
          {/* Row 2: Action Buttons - grid layout */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-indigo-100">
              <Button 
                size="sm" 
                variant="outline"
                onClick={async () => {
                  try {
                    toast({
                      title: "Opening Consent",
                      description: "Loading consent PDF...",
                    });

                    const token = localStorage.getItem('dentalToken');
                    const formattedDate = new Date(consent.assignedDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    });

                    const params = new URLSearchParams({
                      patient_name: consent.patientName || '',
                      dentist_name: consent.dentistName || '',
                      date: formattedDate,
                      treatment_refused: consent.treatmentRefused || '',
                      assignment_id: consent.id || ''
                    });

                    const response = await axios.get(
                      `${process.env.REACT_APP_BACKEND_URL}/api/informed-consent/view-pdf/${consent.consentId}?${params.toString()}`,
                      {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                      }
                    );

                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const blobUrl = URL.createObjectURL(blob);

                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                  } catch (error) {
                    console.error('View error:', error);
                    const errorMsg = error.response?.status === 404 
                      ? "This consent template no longer exists. Please reassign a new consent." 
                      : error.response?.data?.detail || "Failed to open consent";
                    toast({
                      title: "View Error",
                      description: errorMsg,
                      variant: "destructive",
                    });
                  }
                }}
                className="text-sm px-3 py-1 h-8"
              >
                View
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => {
                  navigate(`/edit-informed-consent/${consent.consentId}`);
                }}
                className="text-sm px-3 py-1 h-8"
              >
                Edit
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={async () => {
                  try {
                    toast({
                      title: "Preparing Print",
                      description: "Loading consent PDF with patient info...",
                    });

                    const token = localStorage.getItem('dentalToken');

                    // Format date properly
                    const formattedDate = new Date(consent.assignedDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    });

                    console.log('Print consent with:', {
                      patientName: consent.patientName,
                      dentistName: consent.dentistName,
                      date: formattedDate
                    });

                    // Build URL with patient info as query parameters
                    const params = new URLSearchParams({
                      patient_name: consent.patientName || '',
                      dentist_name: consent.dentistName || '',
                      date: formattedDate,
                      treatment_refused: consent.treatmentRefused || '',
                      assignment_id: consent.id || ''
                    });

                    console.log('Print URL:', `${process.env.REACT_APP_BACKEND_URL}/api/informed-consent/view-pdf/${consent.consentId}?${params.toString()}`);

                    const response = await axios.get(
                      `${process.env.REACT_APP_BACKEND_URL}/api/informed-consent/view-pdf/${consent.consentId}?${params.toString()}`,
                      {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                      }
                    );

                    // Create blob URL
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const blobUrl = URL.createObjectURL(blob);

                    // Create hidden iframe for printing (avoids popup blockers)
                    const iframe = document.createElement('iframe');
                    iframe.style.position = 'fixed';
                    iframe.style.right = '0';
                    iframe.style.bottom = '0';
                    iframe.style.width = '0';
                    iframe.style.height = '0';
                    iframe.style.border = 'none';
                    iframe.src = blobUrl;

                    document.body.appendChild(iframe);

                    // Wait for PDF to load, then print immediately
                    iframe.onload = function() {
                      try {
                        iframe.contentWindow.focus();
                        iframe.contentWindow.print();

                        toast({
                          title: "Print Dialog Opened",
                          description: "Complete printing, then this will auto-cleanup",
                          duration: 5000,
                        });

                        // Cleanup function
                        const cleanup = () => {
                          try {
                            if (document.body.contains(iframe)) {
                              document.body.removeChild(iframe);
                            }
                            URL.revokeObjectURL(blobUrl);
                          } catch (e) {
                            console.error('Cleanup error:', e);
                          }
                        };

                        // Cleanup after 2 minutes
                        setTimeout(cleanup, 120000);

                        // Also cleanup when window regains focus
                        window.addEventListener('focus', function focusHandler() {
                          setTimeout(() => {
                            cleanup();
                            window.removeEventListener('focus', focusHandler);
                          }, 1000);
                        }, { once: true });

                      } catch (error) {
                        console.error('Print error:', error);
                        toast({
                          title: "Print Failed",
                          description: "Please try downloading the PDF instead",
                          variant: "destructive",
                        });
                      }
                    };
                  } catch (error) {
                    console.error('Print error:', error);
                    toast({
                      title: "Print Error",
                      description: "Failed to print consent",
                      variant: "destructive",
                    });
                  }
                }}
                className="text-sm px-3 py-1 h-8"
              >
                Print
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={async () => {
                  try {
                    const patient = dashboardData?.recentPatients?.find(p => p.id === consent.patientId);
                    if (!patient?.email) {
                      toast({
                        title: "No Email Address",
                        description: "This patient does not have an email address on file",
                        variant: "destructive",
                      });
                      return;
                    }

                    toast({
                      title: "Sending Email",
                      description: `Sending ${consent.consentName} to ${patient.email}...`,
                    });

                    const token = localStorage.getItem('dentalToken');

                    await axios.post(
                      `${process.env.REACT_APP_BACKEND_URL}/api/informed-consent/email`,
                      {
                        patientEmail: patient.email,
                        patientName: consent.patientName || `${patient.firstName} ${patient.lastName}`,
                        consentId: consent.consentId,
                        consentName: consent.consentName,
                        assignmentId: consent.id
                      },
                      {
                        headers: { Authorization: `Bearer ${token}` }
                      }
                    );

                    toast({
                      title: "✅ Email Sent Successfully",
                      description: `Informed consent sent to ${patient.email}`,
                    });

                    loadDashboard();
                  } catch (error) {
                    console.error('Email error:', error);
                    toast({
                      title: "Email Failed",
                      description: error.response?.data?.detail || "Failed to send email",
                      variant: "destructive",
                    });
                  }
                }}
                className="text-sm px-3 py-1 h-8"
              >
                Email
              </Button>
              <Button
                data-testid={`text-consent-${consent.id}`}
                size="sm" 
                variant="outline"
                onClick={() => {
                  const patient = dashboardData?.recentPatients?.find(p => p.id === consent.patientId);
                  showSmsSelectionModal(
                    consent.patientId,
                    'consent',
                    consent.id,
                    patient?.cellPhone || patient?.phone,
                    `${patient?.firstName || ''} ${patient?.lastName || ''}`.trim(),
                    consent.consentName
                  );
                }}
                className="text-sm px-3 py-1 h-8 ml-1"
              >
                <Smartphone className="h-3 w-3 mr-1" />
                Text
              </Button>
            </div>
            {/* Email/SMS sent indicators */}
            {(consent.emailSent || consent.smsSentAt) && (
              <div className="flex flex-wrap gap-3 mt-2 pt-2 border-t border-indigo-100">
                {consent.lastEmailSentAt && (
                  <p className="text-xs text-green-600 whitespace-nowrap">
                    Emailed: {new Date(consent.lastEmailSentAt).toLocaleDateString()} at {new Date(consent.lastEmailSentAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: true})}
                    {consent.emailSentCount > 1 && ` (${consent.emailSentCount}x)`}
                  </p>
                )}
                {!consent.lastEmailSentAt && consent.emailSentAt && (
                  <p className="text-xs text-green-600 whitespace-nowrap">
                    Emailed: {new Date(consent.emailSentAt).toLocaleDateString()} at {new Date(consent.emailSentAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: true})}
                  </p>
                )}
                {consent.smsSentAt && (
                  <p className="text-xs text-blue-600 whitespace-nowrap">
                    Texted: {new Date(consent.smsSentAt).toLocaleDateString()} at {new Date(consent.smsSentAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: true})}
                  </p>
                )}
              </div>
            )}
        </div>
      ))}
    </div>
  </>
);
